import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getPageBanners } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';
import './PageBanner.css';

// Per-page hero banner images (design-team artwork, 1920x320 / 6:1, navy-left →
// photo-right so the title/eyebrow/subtitle sit legibly over the navy zone).
// Keyed by route; subpages/dynamic routes inherit via prefix match (e.g.
// /iqac/... → /iqac, /blog/:id → /blog, /department/:id → /department).
// Pages not listed here fall back to the navy gradient defined in PageBanner.css.
const HERO_IMAGES = {
  '/about-nagarjuna': '/images/hero/about-nagarjuna.png',
  '/admission-process': '/images/hero/admission-process.png',
  '/alumni': '/images/hero/alumni.png',
  '/approvals-and-disclosure': '/images/hero/approvals-and-disclosure.png',
  '/audit-reports': '/images/hero/audit-reports.png',
  '/autonomous-regulations': '/images/hero/autonomous-regulations.png',
  '/blog': '/images/hero/blog.png',
  '/careers': '/images/hero/careers.png',
  '/contact': '/images/hero/contact.png',
  '/departments': '/images/hero/departments.png',
  '/department': '/images/hero/departments.png',
  '/examination': '/images/hero/examination.png',
  '/gallery': '/images/hero/gallery.png',
  '/iic': '/images/hero/iic.png',
  '/iqac': '/images/hero/iqac.png',
  '/monthly-reports': '/images/hero/monthly-reports.png',
  '/nba-visit-data': '/images/hero/nba-visit-data.png',
  '/ncc': '/images/hero/ncc.png',
  '/placements': '/images/hero/placements.png',
  '/samashti': '/images/hero/samashti.png',
};

// Match a pathname against a set of route keys: exact match first, then the
// longest registered prefix (so subpages/detail routes inherit their section).
const matchKey = (pathname, keys) => {
  if (keys.includes(pathname)) return pathname;
  return keys
    .filter((k) => pathname === k || pathname.startsWith(k + '/'))
    .sort((a, b) => b.length - a.length)[0] || null;
};

// Resolve a route to its STATIC fallback banner image.
const resolveHeroImage = (pathname) => {
  const key = matchKey(pathname, Object.keys(HERO_IMAGES));
  return key ? HERO_IMAGES[key] : null;
};

// Admin-managed banner overrides (route→url), fetched once per session and
// cached at module scope so later navigations resolve synchronously (no flash).
let _dynamicBanners = null;
const resolveDynamicBanner = (pathname, map) => {
  if (!map) return null;
  const key = matchKey(pathname, Object.keys(map));
  const raw = key ? map[key] : null;
  return raw ? mediaUrl(raw) : null;
};

const PageBanner = ({ title, eyebrow, facts = [], breadcrumbs = [], subtitle, image, className = "", style = {}, children }) => {
  const titleParts = title ? title.trim().split(" ") : [];
  const titleLast = titleParts.pop();

  const location = useLocation();

  // Admin-managed banner overrides. Start from the module cache (instant on
  // repeat visits); fetch once per session on first mount.
  const [dynamicBanners, setDynamicBanners] = useState(_dynamicBanners);
  useEffect(() => {
    if (_dynamicBanners) return; // already loaded this session
    let active = true;
    (async () => {
      try {
        const res = await getPageBanners();
        // getRequest returns the parsed body { success, data: <map> }.
        const map = res?.data || {};
        _dynamicBanners = map;
        if (active) setDynamicBanners(map);
      } catch (e) {
        /* keep static fallback on error */
      }
    })();
    return () => { active = false; };
  }, []);

  // Precedence: explicit `image` prop (page's own content) > admin override >
  // static route→image map > navy gradient.
  const heroImage =
    image ||
    resolveDynamicBanner(location.pathname, dynamicBanners) ||
    resolveHeroImage(location.pathname);

  // When a photo banner is used, layer a left→right navy scrim over it so the
  // text zone (left ~55%) stays legible over ANY artwork — including banners that
  // fade to white on the right (e.g. the approvals credentials graphic) — while
  // the right-side subject (past ~68%) stays clear.
  const photoStyle = heroImage
    ? {
        backgroundColor: '#0a1a3f',
        backgroundImage: `linear-gradient(90deg, rgba(10, 26, 63, 0.86) 0%, rgba(10, 26, 63, 0.58) 38%, rgba(10, 26, 63, 0.22) 55%, rgba(10, 26, 63, 0) 72%), url("${heroImage}")`,
        backgroundSize: 'cover',
        // Anchor right: every banner's subject sits on the right, so cropping (on
        // screens narrower than 1920) happens on the left navy — never the subject.
        backgroundPosition: 'right center',
        backgroundRepeat: 'no-repeat',
      }
    : {};

  return (
    <section
      className={`page-banner ${heroImage ? 'page-banner--photo' : ''} ${className}`}
      style={{ ...photoStyle, ...style }}
    >
      <div className="container">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="page-banner__crumbs" aria-label="Breadcrumb">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="page-banner__crumbs-sep">/</span>}
                {crumb.path ? (
                  <Link to={crumb.path}>{crumb.label}</Link>
                ) : (
                  <span aria-current="page">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}
        {eyebrow && <span className="page-banner__eyebrow">{eyebrow}</span>}
        <h1 className="page-banner__title">
          {titleParts.join(" ")} {titleLast && <span>{titleLast}</span>}
        </h1>
        {subtitle && <p className="page-banner__subtitle">{subtitle}</p>}
        {facts && facts.length > 0 && (
          <ul className="page-banner__facts">
            {facts.map((f, idx) => <li key={idx}>{f}</li>)}
          </ul>
        )}
        {children}
      </div>
    </section>
  );
};

export default PageBanner;
